import { PlayerRatingScope, PlayerIdentity } from "components/ReviewAudit/PublicRatings"
import React, { useCallback, useEffect, useMemo } from "react"
import { connect } from "react-redux"
import { useLocation, useHistory, Link } from "react-router-dom"
import { Container, Card, CardBody, Button } from "reactstrap"
import { useToasts } from "react-toast-notifications"
import ListenerComponent from "components/Common/Socket/ListenerComponent"
import PastLastPage, { isPastLastPage } from "components/Common/PastLastPage"
import { getFriendlyMatches } from "./actions"
import AdvancedFilters from "./AdvancedFilters"
import MatchSummary from "./MatchSummary"
import { matchPeople } from "./MatchSummary"
import "./friendlyMatches.scss"
import "../../assets/scss/custom/components/_directoryActions.scss"

const filterKeys = ["search", "sportType", "gameType", "matchType", "location", "status", "score", "fromDate", "toDate"]
const FriendlyMatches = ({ fetchFriendlyMatches, friendlyMatchesState }) => {
  const location = useLocation(), history = useHistory()
  const activeFilters = useMemo(() => {
    const query = new URLSearchParams(location.search)
    return Object.fromEntries(filterKeys.filter(key => query.get(key)).map(key => [key, query.get(key)]))
  }, [location.search])
  const options = useMemo(() => {
    const page = Number(new URLSearchParams(location.search).get("page"))
    return { page: Number.isSafeInteger(page) && page > 0 && page <= 100000 ? page : 1, limit: 15 }
  }, [location.search])
  useEffect(() => { fetchFriendlyMatches(activeFilters, options) }, [activeFilters, options, fetchFriendlyMatches])
  const changeFilter = useCallback((key, value) => {
    const query = new URLSearchParams(history.location.search)
    query.delete("page")
    value ? query.set(key, value) : query.delete(key)
    history.replace({ pathname: location.pathname, search: query.toString() })
  }, [history, location.pathname])
  const detailTo = match => ({ pathname: `/friendly-matches/${match._id}`, state: { friendlyMatchesReturnTo: location.pathname + location.search } })
  const pageTo = page => history.push({ pathname: location.pathname, search: new URLSearchParams({ ...activeFilters, page }).toString() })
  const { addToast } = useToasts()
  const reload = () => fetchFriendlyMatches(activeFilters, options)
  const listenForResponse = ({ success, message }) => {
    if (success) reload()
    addToast(message, { appearance: success ? "success" : "error", autoDismiss: true })
  }
  const { friendlyMatches: data = {}, loading, error } = friendlyMatchesState
  // Back from a deleted match, or a bookmark, can land past the last page while the Match status set shrank.
  const pastEnd = isPastLastPage(options.page, data)
  return <PlayerRatingScope players={!loading && !error ? (data.results || []).flatMap(matchPeople) : []}><div className="page-content fm-page"><Container fluid>
    <ListenerComponent listenFunction={listenForResponse} topic="friendly-matches-response" />
    <Card><CardBody>
      <h4>Friendly Matches</h4>
      <AdvancedFilters values={activeFilters} onChange={changeFilter} locations={data.locations || []} />
      {Object.keys(activeFilters).length > 0 && <Button size="sm" color="light" className="mb-3" onClick={() => history.replace(location.pathname)}>Clear filters</Button>}
      {loading && <p role="status">Loading matches…</p>}
      {error && <div role="alert" className="alert alert-danger">Matches could not be loaded. <Button onClick={reload}>Retry</Button></div>}
      {!loading && !error && <>
        <p>{data.totalResults ?? 0} {data.totalResults === 1 ? "match" : "matches"} · Match date, newest first</p>
        {data.results?.length ? <div>{data.results.map(match => <MatchSummary key={match._id} match={match} detailTo={detailTo(match)} actions={<>
          <Link className="btn btn-outline-primary btn-sm directory-action-button" to={detailTo(match)}>Open</Link>

        </>} />)}</div> : pastEnd ? <PastLastPage onFirstPage={() => pageTo(1)} /> : <p>No matches found.</p>}
        {data.totalPages > 1 && !pastEnd && <nav aria-label="Match pages" className="d-flex align-items-center justify-content-center mt-3">
          <Button color="light" disabled={options.page <= 1} onClick={() => pageTo(options.page - 1)}>Previous</Button>
          <span className="mx-3">Page {options.page} of {data.totalPages}</span>
          <Button color="light" disabled={options.page >= data.totalPages} onClick={() => pageTo(options.page + 1)}>Next</Button>
        </nav>}
      </>}
    </CardBody></Card>
  </Container></div></PlayerRatingScope>
}
export default connect(({ friendlyMatches = {} }) => ({ friendlyMatchesState: friendlyMatches }), {
  fetchFriendlyMatches: (filters, options) => getFriendlyMatches(filters, options),
})(FriendlyMatches)
